import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { type StripeEnv, createStripeClient, getStripeErrorMessage } from "@/lib/stripe.server";

type CheckoutSessionResult =
  | { clientSecret: string }
  | { error: string; code?: "already_premium" | "invalid_price" | "unauthorized" | "no_chef_profile" };
type PortalSessionResult = { url: string } | { error: string };

// Whitelist dei Price ID consentiti per il checkout Premium.
// Sprint 19.1 — supporto Silver/Gold; gli ID legacy restano per i rinnovi.
const ALLOWED_PRICE_IDS = new Set<string>([
  "chef_silver_monthly",
  "chef_gold_monthly",
  // Legacy (chiusi a nuovi acquisti dal listing pubblico, ma mantenuti
  // per i clienti già attivi che li rinnovano):
  "chef_premium_monthly",
  "chef_premium_yearly",
]);

/** Rank crescente fra i tier — usato per consentire upgrade Silver→Gold. */
const PRICE_TIER_RANK: Record<string, number> = {
  chef_premium_monthly: 1,
  chef_premium_yearly: 1,
  chef_silver_monthly: 1,
  chef_gold_monthly: 2,
};

/**
 * Sprint 24.5 — fallback per le subscription `launch` (Founder) o legacy,
 * dove `price_id` è NULL ma la colonna `plan` indica comunque il tier
 * (es. `silver_monthly`). Senza questo fallback un upgrade Silver→Gold
 * funzionerebbe per puro caso (default rank=1 == rank Silver), ma il
 * controllo non sarebbe corretto. Lo allineiamo esplicitamente.
 */
const PLAN_TIER_RANK: Record<string, number> = {
  silver_monthly: 1,
  gold_monthly: 2,
  premium_monthly: 1,
  premium_yearly: 1,
  free: 0,
};

async function resolveOrCreateCustomer(
  stripe: ReturnType<typeof createStripeClient>,
  options: { email?: string; userId?: string },
): Promise<string> {
  if (options.userId && !/^[a-zA-Z0-9_-]+$/.test(options.userId)) {
    throw new Error("Invalid userId");
  }
  if (options.userId) {
    const found = await stripe.customers.search({
      query: `metadata['userId']:'${options.userId}'`,
      limit: 1,
    });
    if (found.data.length) return found.data[0].id;
  }
  if (options.email) {
    const existing = await stripe.customers.list({ email: options.email, limit: 1 });
    if (existing.data.length) {
      const customer = existing.data[0];
      if (options.userId && customer.metadata?.userId !== options.userId) {
        await stripe.customers.update(customer.id, {
          metadata: { ...customer.metadata, userId: options.userId },
        });
      }
      return customer.id;
    }
  }
  const created = await stripe.customers.create({
    ...(options.email && { email: options.email }),
    ...(options.userId && { metadata: { userId: options.userId } }),
  });
  return created.id;
}

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data: {
    priceId: string;
    returnUrl: string;
    environment: StripeEnv;
  }) => {
    if (!/^[a-zA-Z0-9_-]+$/.test(data.priceId)) throw new Error("Invalid priceId");
    if (!ALLOWED_PRICE_IDS.has(data.priceId)) throw new Error("Invalid priceId");
    if (typeof data.returnUrl !== "string" || !/^https?:\/\//.test(data.returnUrl)) {
      throw new Error("Invalid returnUrl");
    }
    if (data.environment !== "sandbox" && data.environment !== "live") {
      throw new Error("Invalid environment");
    }
    return data;
  })
  .handler(async ({ data, context }): Promise<CheckoutSessionResult> => {
    try {
      // SECURITY: l'utente proviene SOLO dal token autenticato, mai dal client.
      const userId = context.userId;
      const customerEmail =
        typeof context.claims?.email === "string" ? (context.claims.email as string) : undefined;

      // HOTFIX P0: Premium è riservato agli chef. Senza riga `chefs` la
      // sottoscrizione fallirebbe per FK (subscriptions.chef_id → chefs.user_id)
      // e l'utente resterebbe in uno stato incoerente (notifica "Premium attivo"
      // ma dashboard senza badge/founder). Blocchiamo prima del checkout.
      const { data: chefRow } = await context.supabase
        .from("chefs")
        .select("user_id")
        .eq("user_id", userId)
        .maybeSingle();
      if (!chefRow) {
        return {
          error: "Per attivare Premium devi prima creare il tuo profilo Chef.",
          code: "no_chef_profile",
        };
      }

      // Blocca la creazione se esiste già un abbonamento Premium attivo
      // per lo stesso utente nello stesso ambiente — eccezione: upgrade
      // verso un tier superiore (es. Silver → Gold) è sempre consentito.
      const nowIso = new Date().toISOString();
      const { data: activeSub } = await context.supabase
        .from("subscriptions")
        .select("status, current_period_end, expires_at, cancel_at_period_end, price_id, plan")
        .eq("chef_id", userId)
        .eq("environment", data.environment)
        .eq("status", "active")
        .or(`current_period_end.gt.${nowIso},expires_at.gt.${nowIso}`)
        .limit(1)
        .maybeSingle();
      if (activeSub) {
        const currentRank =
          PRICE_TIER_RANK[activeSub.price_id as string] ??
          PLAN_TIER_RANK[(activeSub as { plan?: string | null }).plan ?? ""] ??
          1;
        const targetRank = PRICE_TIER_RANK[data.priceId] ?? 1;
        // Consenti solo upgrade verso un tier strettamente superiore.
        if (targetRank <= currentRank) {
          return {
            error: "Hai già un abbonamento Premium attivo. Gestiscilo dal Customer Portal.",
            code: "already_premium",
          };
        }
      }

      const stripe = createStripeClient(data.environment);
      const prices = await stripe.prices.list({ lookup_keys: [data.priceId] });
      if (!prices.data.length) throw new Error("Prezzo non trovato");
      const stripePrice = prices.data[0];
      const isRecurring = stripePrice.type === "recurring";

      const customerId = await resolveOrCreateCustomer(stripe, {
        email: customerEmail,
        userId,
      });

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: isRecurring ? "subscription" : "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        managed_payments: { enabled: true },
        metadata: { userId },
        ...(isRecurring && { subscription_data: { metadata: { userId } } }),
      } as any);

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const createPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((data: { returnUrl?: string; environment: StripeEnv }) => data)
  .handler(async ({ data, context }): Promise<PortalSessionResult> => {
    const { supabase, userId } = context;
    const { data: sub, error: subError } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("chef_id", userId)
      .eq("environment", data.environment)
      .not("stripe_customer_id", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (subError || !sub?.stripe_customer_id) {
      return { error: "Nessun abbonamento Stripe trovato" };
    }
    try {
      const stripe = createStripeClient(data.environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: sub.stripe_customer_id as string,
        ...(data.returnUrl && { return_url: data.returnUrl }),
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });